import React, { useRef, useState, useEffect } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ChevronRight, Zap, Globe, Shield, Trophy, Cpu, Target, Accessibility } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { GlassButton } from '../components/ui/glass-button';
import { ShaderAnimation } from '../components/ui/shader-animation';
import { Drone3D } from '../components/ui/Drone3D';
import droneVision from '../assets/drone-vision.png';
import droneImg from '../assets/drone.png';
import drone2Img from '../assets/drone2.png';
import drone3Img from '../assets/drone3.png';
import logoImg from '../assets/logo.png';

// CSS Cloud (lightweight)
const AnimatedCloud = ({ top, delay, duration, opacity, scale }: { top: string, delay: number, duration: number, opacity: number, scale: number }) => (
  <motion.div
    initial={{ x: "-30vw" }}
    animate={{ x: "120vw" }}
    transition={{ repeat: Infinity, duration, ease: "linear", delay }}
    style={{ position: 'absolute', pointerEvents: 'none', top, opacity, transform: `scale(${scale})`, zIndex: 0 }}
  >
    <div style={{ position: 'relative', width: '280px', height: '80px', background: '#fff', borderRadius: '100px', filter: 'blur(16px)' }}>
      <div style={{ position: 'absolute', top: '-50px', left: '40px', width: '100px', height: '100px', background: '#fff', borderRadius: '50%' }} />
      <div style={{ position: 'absolute', top: '-80px', left: '100px', width: '140px', height: '140px', background: '#fff', borderRadius: '50%' }} />
      <div style={{ position: 'absolute', top: '-30px', left: '200px', width: '80px', height: '80px', background: '#fff', borderRadius: '50%' }} />
    </div>
  </motion.div>
);

// CSS-only ambient background (no WebGL)
const AmbientBackground = () => (
  <div className="absolute inset-0 z-0 pointer-events-none overflow-hidden">
    <div style={{ position: 'absolute', top: '8%', left: '4%', width: 400, height: 400, background: 'radial-gradient(circle, rgba(14,165,233,0.07) 0%, transparent 70%)', borderRadius: '50%' }} />
    <div style={{ position: 'absolute', top: '45%', right: '6%', width: 320, height: 320, background: 'radial-gradient(circle, rgba(230,81,0,0.05) 0%, transparent 70%)', borderRadius: '50%' }} />
    <div style={{ position: 'absolute', bottom: '10%', left: '12%', width: 360, height: 360, background: 'radial-gradient(circle, rgba(14,165,233,0.04) 0%, transparent 70%)', borderRadius: '50%' }} />
  </div>
);

// STATS for the YENİ NESİL SPOR section
const STATS = [
  { value: '0.1ms', label: 'Gecikme', icon: Zap },
  { value: '12+', label: 'Ülke', icon: Globe },
  { value: 'N-TYPE', label: 'Kapsül', icon: Shield },
  { value: '#1', label: 'Türkiye', icon: Trophy },
];

// ────────────────────────────────────────────────────────────
// PREMIUM "YENİ NESİL SPOR" SECTION — Home FeaturedSection tarzı
// Sol: metin + stats  |  Sağ: küçük görsel kart
// ────────────────────────────────────────────────────────────
const NewGenSportSection: React.FC = () => {
  const ref = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start end', 'end start'] });
  const imgY = useTransform(scrollYProgress, [0, 1], ['-6%', '6%']);
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 1024);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  if (isMobile) {
    return (
      <section id="hakkimizda" ref={ref} style={{ position: 'relative', width: '100%', backgroundColor: '#ffffff', padding: '60px 20px', overflow: 'hidden' }}>
        {/* Background Ghost Number - Scaled down */}
        <div style={{
          position: 'absolute',
          top: '10px',
          left: '-15px',
          fontFamily: 'var(--font-headline)',
          fontWeight: 900,
          fontSize: '140px',
          color: 'rgba(15,45,94,0.06)',
          lineHeight: 1,
          userSelect: 'none',
          pointerEvents: 'none',
          letterSpacing: '-0.05em',
          zIndex: 1
        }}></div>

        <div style={{ position: 'relative', zIndex: 10, display: 'flex', flexDirection: 'column', gap: '32px' }}>
          {/* Mobile Image */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            style={{ position: 'relative', width: '100%', height: '260px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          >
            <div style={{ position: 'absolute', top: '15px', left: '10px', right: '-10px', bottom: '-15px', borderRadius: '24px', backgroundColor: '#e65100', opacity: 0.12, transform: 'rotate(4deg)', zIndex: 0 }} />
            <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, borderRadius: '24px', overflow: 'hidden', boxShadow: '0 20px 45px rgba(0,0,0,0.2)', transform: 'rotate(-1.5deg)', backgroundColor: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 5 }}>
              <img
                src={logoImg}
                alt="Hakkımızda"
                loading="eager"
                fetchPriority="high"
                decoding="sync"
                style={{ width: '75%', height: '75%', objectFit: 'contain', display: 'block' }}
              />
              <div style={{ position: 'absolute', top: '15px', left: '15px', width: '25px', height: '25px', borderTop: '2px solid #e65100', borderLeft: '2px solid #e65100' }} />
              <div style={{ position: 'absolute', bottom: '15px', right: '15px', width: '25px', height: '25px', borderBottom: '2px solid #e65100', borderRight: '2px solid #e65100' }} />

            </div>
          </motion.div>

          {/* Mobile Text Section */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', backgroundColor: 'rgba(230,81,0,0.1)', border: '1px solid rgba(230,81,0,0.3)', borderRadius: '99px', padding: '6px 14px', alignSelf: 'flex-start' }}>
              <span style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: '#e65100', boxShadow: '0 0 8px #e65100' }} />
              <span style={{ color: '#e65100', fontSize: '10px', fontWeight: 900, letterSpacing: '2px', textTransform: 'uppercase' }}>HAKKIMIZDA</span>
            </div>

            <h2 style={{ display: 'flex', flexDirection: 'column', gap: '2px', fontFamily: 'var(--font-headline)', fontWeight: 900, textTransform: 'uppercase', margin: 0 }}>
              <span style={{ color: '#e65100', fontSize: '32px', lineHeight: 1.1 }}>BİZİM</span>
              <span style={{ color: '#0f2d5e', fontSize: '32px', lineHeight: 0.9, letterSpacing: '-1.5px' }}>HAKKIMIZDA</span>
            </h2>

            <div style={{ height: '2px', width: '60px', backgroundColor: '#e65100', borderRadius: '2px' }} />

            <p style={{ fontSize: '15px', color: '#0f2d5e', lineHeight: 1.6, fontWeight: 500 }}>
              Eğitime gönül vermiş bir grup idealist eğitimci ve iş sahibi insanlar olarak fütürist bir bakış açısıyla düşünerek <strong style={{ color: '#e65100' }}>Flying Slap Spor Kulübü</strong>’nü gençlerimize yeni kapılar açmak yeni fırsatlar sunmak için kurduk.
              <span style={{ display: 'block', marginTop: '12px', color: '#0f2d5e', fontWeight: 600 }}>
                Flying Slap takımına katılan öğrencilerimiz, İl Gençlik ve Spor Müdürlüğü’nden sporcu lisansı almaya hak kazanacaklar ve profesyonel sporcu kabul edilerek ulusal ve uluslararası arenalarda yarışma ve turnuvalara katılabileceklerdir.
              </span>
              <span style={{ display: 'block', marginTop: '12px', color: '#0f2d5e', fontWeight: 800, fontSize: '15px' }}>
                Amacımız; ülkemizin ve geleceğimizin teminatı olan Türk gençliğini 21. yüzyıl becerileriyle donanımlı şekilde yetiştirmektir. Bugün henüz adını bile bilmediğimiz mesleklerle karşılaşacak olan gençlerimize, sağlam temeller atabilecekleri fırsatlar sunmayı hedefliyoruz.
              </span>
            </p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="hakkimizda" ref={ref} className="relative w-full overflow-hidden" style={{ minHeight: 480, background: '#ffffff' }}>
      <div style={{ position: 'absolute', top: -40, right: -20, fontFamily: 'var(--font-headline)', fontWeight: 900, fontSize: 'clamp(140px, 20vw, 240px)', color: 'rgba(15,45,94,0.04)', lineHeight: 1, userSelect: 'none', pointerEvents: 'none', letterSpacing: '-0.05em', zIndex: 0 }}></div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-16 py-12 grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
        <motion.div initial={{ opacity: 0, x: -50 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 1, ease: 'easeOut' }} style={{ position: 'relative', height: 380, flexShrink: 0, width: '100%' }}>
          <div style={{ position: 'absolute', inset: 0, borderRadius: 20, background: 'rgba(230,81,0,0.18)', transform: 'rotate(-4deg) scale(0.96)', top: 16, left: -8 }} />
          <motion.div style={{ y: imgY, position: 'absolute', inset: 0, borderRadius: 20, overflow: 'hidden', boxShadow: '0 32px 80px rgba(0,0,0,0.5)', transform: 'rotate(1.5deg)' }} whileHover={{ rotate: 0, transition: { duration: 0.4 } }}>
            <img src={logoImg} alt="Hakkımızda" loading="eager" fetchPriority="high" decoding="sync" style={{ width: '100%', height: '100%', objectFit: 'contain', transform: 'scale(1.05)', background: '#fff' }} />
            <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(160deg, transparent 40%, rgba(15,45,94,0.3) 100%)' }} />
            <div style={{ position: 'absolute', top: 16, left: 16, width: 32, height: 32, borderTop: '2px solid rgba(230,81,0,0.8)', borderLeft: '2px solid rgba(230,81,0,0.8)' }} />
            <div style={{ position: 'absolute', bottom: 16, right: 16, width: 32, height: 32, borderBottom: '2px solid rgba(230,81,0,0.8)', borderRight: '2px solid rgba(230,81,0,0.8)' }} />

          </motion.div>
        </motion.div>

        <div className="flex flex-col gap-8">
          <motion.div initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: 'rgba(230,81,0,0.1)', border: '1px solid rgba(230,81,0,0.3)', borderRadius: 99, padding: '4px 10px', alignSelf: 'flex-start' }}>
            <span style={{ width: 5, height: 5, borderRadius: '50%', background: '#e65100', boxShadow: '0 0 8px #e65100' }} />
            <span style={{ color: '#e65100', fontSize: 10, fontWeight: 900, letterSpacing: '0.15em', textTransform: 'uppercase' }}>HAKKIMIZDA</span>
          </motion.div>
          <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.8, ease: 'easeOut' }}>
            <h2 className="font-black font-headline uppercase tracking-tighter leading-none" style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span style={{ color: '#e65100', fontSize: 'clamp(2.2rem, 3.5vw, 3rem)' }}>BİZİM</span>
              <span style={{ color: '#0f2d5e', fontSize: 'clamp(2.2rem, 3.5vw, 3rem)' }}>HAKKIMIZDA</span>
            </h2>
          </motion.div>
          <motion.div initial={{ scaleX: 0 }} whileInView={{ scaleX: 1 }} transition={{ duration: 0.8, delay: 0.3 }} style={{ height: 2, background: 'linear-gradient(to right, #e65100, transparent)', transformOrigin: 'left', width: 60 }} />
          <motion.p initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }} transition={{ delay: 0.3, duration: 0.6 }} style={{ color: '#0f2d5e', fontSize: '0.9rem', lineHeight: 1.7, maxWidth: 460 }}>
            Eğitime gönül vermiş bir grup idealist eğitimci ve iş sahibi insanlar olarak fütürist bir bakış açısıyla düşünerek <strong style={{ color: '#e65100' }}>Flying Slap Spor Kulübü</strong>’nü gençlerimize yeni kapılar açmak yeni fırsatlar sunmak için kurduk.
            <span style={{ display: 'block', marginTop: 12, color: '#0f2d5e', fontWeight: 600 }}>
              Flying Slap takımına katılan öğrencilerimiz, İl Gençlik ve Spor Müdürlüğü’nden sporcu lisansı almaya hak kazanacaklar ve profesyonel sporcu kabul edilerek ulusal ve uluslararası arenalarda yarışma ve turnuvalara katılabileceklerdir.
            </span>
            <span style={{ display: 'block', marginTop: 12, color: '#0f2d5e', fontWeight: 700 }}>
              Amacımız; ülkemizin ve geleceğimizin teminatı olan Türk gençliğini 21. yüzyıl becerileriyle donanımlı şekilde yetiştirmektir. Bugün henüz adını bile bilmediğimiz mesleklerle karşılaşacak olan gençlerimize, sağlam temeller atabilecekleri fırsatlar sunmayı hedefliyoruz.
            </span>
          </motion.p>
        </div>
      </div>
    </section>
  );
};

// ────────────────────────────────────────────────────────────
// DARK EDITORIAL SECTION (aynı stil — 02 & 03 için)
// ────────────────────────────────────────────────────────────
interface DarkSectionProps {
  image: string;
  number: string;
  label: string;
  title: string;
  subtitle: string;
  text: React.ReactNode;
  features: { icon: React.ComponentType<{ style?: React.CSSProperties }>; text: string }[];
  badge?: { value: string; label: string };
  reversed?: boolean;
  titleColor?: string;
  subtitleColor?: string;
}

const DarkSection: React.FC<DarkSectionProps> = ({ image, number, label, title, subtitle, text, features, badge, reversed = false, titleColor, subtitleColor }) => {
  const ref = useRef<HTMLElement>(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start end', 'end start'] });
  const imgY = useTransform(scrollYProgress, [0, 1], ['-6%', '6%']);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 1024);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  if (isMobile) {
    return (
      <section ref={ref} style={{ position: 'relative', width: '100%', backgroundColor: '#ffffff', padding: '60px 20px', overflow: 'hidden' }}>
        {/* Background Ghost Number - Scaled down */}
        <div style={{
          position: 'absolute',
          top: '10px',
          [reversed ? 'right' : 'left']: '-15px',
          fontFamily: 'var(--font-headline)',
          fontWeight: 900,
          fontSize: '140px',
          color: 'rgba(15,45,94,0.06)',
          lineHeight: 1,
          userSelect: 'none',
          pointerEvents: 'none',
          letterSpacing: '-0.05em',
          zIndex: 1
        }}></div>

        <div style={{ position: 'relative', zIndex: 10, display: 'flex', flexDirection: 'column', gap: '32px' }}>
          {/* Mobile Image - Scaled down */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            style={{ position: 'relative', width: '100%', height: '260px', display: 'block' }}
          >
            {/* Design accents */}
            <div style={{ position: 'absolute', top: '15px', [reversed ? 'right' : 'left']: '10px', [reversed ? 'left' : 'right']: '-10px', bottom: '-15px', borderRadius: '24px', backgroundColor: '#e65100', opacity: 0.12, transform: reversed ? 'rotate(-4deg)' : 'rotate(4deg)', zIndex: 0 }} />

            {/* Image Container */}
            <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, borderRadius: '24px', overflow: 'hidden', boxShadow: '0 20px 45px rgba(0,0,0,0.2)', transform: reversed ? 'rotate(1.5deg)' : 'rotate(-1.5deg)', backgroundColor: '#0f2d5e', zIndex: 5 }}>
              <img
                src={image}
                alt={title}
                style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
              />
              <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, background: 'linear-gradient(to top, rgba(15,45,94,0.85) 0%, transparent 70%)' }} />

              {/* Corner Borders */}
              <div style={{ position: 'absolute', top: '15px', left: '15px', width: '25px', height: '25px', borderTop: '2px solid #e65100', borderLeft: '2px solid #e65100' }} />
              <div style={{ position: 'absolute', bottom: '15px', right: '15px', width: '25px', height: '25px', borderBottom: '2px solid #e65100', borderRight: '2px solid #e65100' }} />

              {/* Caption */}

            </div>


          </motion.div>

          {/* Mobile Text Section - Compact Typography */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', backgroundColor: 'rgba(230,81,0,0.1)', border: '1px solid rgba(230,81,0,0.3)', borderRadius: '99px', padding: '6px 14px', alignSelf: 'flex-start' }}>
              <span style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: '#e65100', boxShadow: '0 0 8px #e65100' }} />
              <span style={{ color: '#e65100', fontSize: '10px', fontWeight: 900, letterSpacing: '2px', textTransform: 'uppercase' }}>{label}</span>
            </div>

            <h2 style={{ display: 'flex', flexDirection: 'column', gap: '2px', fontFamily: 'var(--font-headline)', fontWeight: 900, textTransform: 'uppercase', margin: 0 }}>
              <span style={{ color: titleColor || 'rgba(15,45,94,0.3)', fontSize: '32px', lineHeight: 1.1 }}>{title}</span>
              <span style={{ color: subtitleColor || '#0f2d5e', fontSize: '32px', lineHeight: 0.9, letterSpacing: '-1.5px' }}>{subtitle}</span>
            </h2>

            <div style={{ height: '2px', width: '60px', backgroundColor: '#e65100', borderRadius: '2px' }} />

            <p style={{ fontSize: '15px', color: '#0f2d5e', lineHeight: 1.6, fontWeight: 500 }}>{text}</p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '4px' }}>
              {features.map((item, idx) => (
                <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div style={{ width: '30px', height: '30px', borderRadius: '8px', backgroundColor: 'rgba(230,81,0,0.08)', border: '1px solid rgba(230,81,0,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <item.icon style={{ width: '15px', height: '15px', color: '#e65100' }} />
                  </div>
                  <span style={{ fontSize: '13px', fontWeight: 600, color: '#0f2d5e' }}>{item.text}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    );
  }

  const textBlock = (
    <div className="flex flex-col gap-8">
      <motion.div initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: 'rgba(230,81,0,0.15)', border: '1px solid rgba(230,81,0,0.35)', borderRadius: 99, padding: '4px 10px', alignSelf: 'flex-start' }}>
        <span style={{ width: 5, height: 5, borderRadius: '50%', background: '#e65100', display: 'block', animation: 'pulse 1.5s infinite' }} />
        <span style={{ color: '#e65100', fontSize: 10, fontWeight: 900, letterSpacing: '0.5em', textTransform: 'uppercase' }}>{label}</span>
      </motion.div>
      <motion.div initial={{ opacity: 0, x: reversed ? 30 : -30 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.8, ease: 'easeOut' }}>
        <h2 className="font-black font-headline uppercase tracking-tighter leading-none" style={{ fontSize: 'clamp(2rem, 3.5vw, 3rem)' }}>
          <span style={{ display: 'block', color: titleColor || 'rgba(15,45,94,0.35)', marginBottom: 8 }}>{title}</span>
          <span style={{ display: 'block', color: subtitleColor || '#0f2d5e' }}>{subtitle}</span>
        </h2>
      </motion.div>
      <motion.div initial={{ scaleX: 0 }} whileInView={{ scaleX: 1 }} transition={{ duration: 0.8, delay: 0.3 }} style={{ height: 1, background: 'linear-gradient(to right, rgba(15,45,94,0.15), transparent)', transformOrigin: reversed ? 'right' : 'left' }} />
      <motion.p initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }} transition={{ delay: 0.3, duration: 0.6 }} style={{ color: '#0f2d5e', fontSize: '0.9rem', lineHeight: 1.7, maxWidth: 460 }}>{text}</motion.p>
      <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} transition={{ delay: 0.4 }} className="flex flex-col gap-3">
        {features.map((item, idx) => (
          <motion.div key={idx} initial={{ opacity: 0, x: reversed ? 16 : -16 }} whileInView={{ opacity: 1, x: 0 }} transition={{ delay: 0.45 + idx * 0.08 }} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 28, height: 28, borderRadius: 8, background: 'rgba(230,81,0,0.18)', border: '1px solid rgba(230,81,0,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <item.icon style={{ width: 12, height: 12, color: '#e65100' }} />
            </div>
            <span style={{ color: '#0f2d5e', fontSize: '0.78rem', fontWeight: 600 }}>{item.text}</span>
          </motion.div>
        ))}
      </motion.div>
    </div>
  );

  const imageBlock = (
    <motion.div initial={{ opacity: 0, x: reversed ? -50 : 50 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 1, ease: 'easeOut' }} style={{ position: 'relative', height: 380, flexShrink: 0, width: '100%' }}>
      <div style={{ position: 'absolute', inset: 0, borderRadius: 20, background: 'rgba(230,81,0,0.18)', transform: reversed ? 'rotate(-4deg) scale(0.96)' : 'rotate(4deg) scale(0.96)', top: 16, left: reversed ? -8 : 8 }} />
      <motion.div style={{ y: imgY, position: 'absolute', inset: 0, borderRadius: 20, overflow: 'hidden', boxShadow: '0 32px 80px rgba(0,0,0,0.5)', transform: reversed ? 'rotate(1.5deg)' : 'rotate(-1.5deg)' }} whileHover={{ rotate: 0, transition: { duration: 0.4 } }}>
        <img src={image} alt={title} style={{ width: '100%', height: '100%', objectFit: 'cover', transform: 'scale(1.05)' }} />
        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(160deg, transparent 40%, rgba(15,45,94,0.8) 100%)' }} />
        <div style={{ position: 'absolute', top: 16, left: 16, width: 32, height: 32, borderTop: '2px solid rgba(230,81,0,0.8)', borderLeft: '2px solid rgba(230,81,0,0.8)' }} />
        <div style={{ position: 'absolute', bottom: 16, right: 16, width: 32, height: 32, borderBottom: '2px solid rgba(230,81,0,0.8)', borderRight: '2px solid rgba(230,81,0,0.8)' }} />

      </motion.div>

    </motion.div>
  );

  return (
    <section ref={ref} className="relative w-full overflow-hidden" style={{ minHeight: 480, background: '#ffffff' }}>
      <div style={{ position: 'absolute', top: -40, [reversed ? 'right' : 'left']: -20, fontFamily: 'var(--font-headline)', fontWeight: 900, fontSize: 'clamp(140px, 20vw, 240px)', color: 'rgba(15,45,94,0.04)', lineHeight: 1, userSelect: 'none', pointerEvents: 'none', letterSpacing: '-0.05em', zIndex: 0 }}></div>
      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-16 py-12 grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
        {reversed ? <>{imageBlock}{textBlock}</> : <>{textBlock}{imageBlock}</>}
      </div>
    </section>
  );
};

// ────────────────────────────────────────────────────────────
// MAIN ABOUT PAGE
// ────────────────────────────────────────────────────────────
const About: React.FC = () => {
  const navigate = useNavigate();
  const containerRef = useRef<HTMLDivElement>(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);

  useEffect(() => {
    document.title = "Flying Slap - Hakkımızda";
    const handleResize = () => setIsMobile(window.innerWidth < 1024);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <div ref={containerRef} className="relative text-gray-900 overflow-hidden font-body min-h-screen">

      <div className="relative z-10">

        {/* 1. HERO */}
        <section id="gokyuzu-bizim" style={{ position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', padding: '96px 24px 80px 24px', overflow: 'hidden' }}>

          <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, zIndex: -2, pointerEvents: 'none', background: 'linear-gradient(to bottom, #87CEEB, #ffffff)' }} />
          <AmbientBackground />

          {/* Autonomous 3D Drone - Absolute Background */}
          <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, zIndex: 0, pointerEvents: 'none', overflow: 'hidden' }}>
            <Drone3D showMain={false} showBackground={true} />
          </div>

          {/* Clouds — reduced to 2 */}
          <AnimatedCloud top="12%" delay={0} duration={75} opacity={0.5} scale={1.2} />
          <AnimatedCloud top="38%" delay={-25} duration={105} opacity={0.35} scale={0.85} />

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
            style={{ textAlign: 'center', zIndex: 10 }}
          >
            <h1 className="font-headline"
              style={{ fontWeight: 900, letterSpacing: '-0.05em', filter: 'drop-shadow(0 4px 6px rgba(0,0,0,0.1))', fontSize: 'clamp(2.5rem, 6.5vw, 4.5rem)', lineHeight: '1.1' }}>
              <span style={{ color: '#e65100', display: 'block' }}>SADECE UÇMUYORUZ,</span>
              <span style={{ color: '#0f2d5e' }}>İLHAM VERİYORUZ</span>
            </h1>
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.3, duration: 0.8 }}
              style={{
                marginTop: '28px',
                padding: '14px 28px',
                borderRadius: '20px',
                background: 'rgba(255, 255, 255, 0.3)',
                backdropFilter: 'blur(12px)',
                border: '1px solid rgba(15, 45, 94, 0.1)',
                boxShadow: '0 20px 40px rgba(15, 45, 94, 0.05)',
                display: 'inline-block',
                position: 'relative',
                zIndex: 10
              }}
            >
              <p style={{ fontSize: '15px', fontWeight: 600, color: '#0f2d5e', opacity: 0.9, maxWidth: '850px', margin: 0, lineHeight: 1.6 }}>
                <span style={{ color: '#e65100' }}>Türkiye Hava Sporları Federasyonu üyesi</span> ve <span style={{ color: '#e65100' }}>Drone Soccer Türkiye partner <span style={{ whiteSpace: 'nowrap' }}>spor kulübü</span></span><br /> olduğumuzu paylaşmaktan memnuniyet duyuyoruz.
              </p>
            </motion.div>
          </motion.div>

          {/* Scroll indicator */}
          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ repeat: Infinity, duration: 2 }}
            style={{ position: 'absolute', bottom: '48px', left: '50%', transform: 'translateX(-50%)', width: '1px', height: '64px', background: 'linear-gradient(to bottom, #e65100, transparent)', zIndex: 5 }}
          />
        </section>

        {/* 2. PREMIUM "YENİ NESİL SPOR" SECTION */}
        <NewGenSportSection />

        <div style={{ height: isMobile ? 24 : 48, backgroundColor: '#ffffff' }} />

        {/* 3. DARK EDITORIAL SECTIONS 02 & 03 */}
        <DarkSection
          image={droneImg}
          number="02"
          label="Hikayemiz"
          title="NEDEN"
          titleColor="#0f2d5e"
          subtitle="FLYING SLAP?"
          subtitleColor="#e65100"
          text={(
            <>
              Türk tokadı ya da popüler adıyla Osmanlı Tokadı aslında ilk Hun Türkleri ve Göktürk’ler tarafından kullanıldığı bilinen, özel eğitim ve disiplin gerektiren efsanevi bir silahsız karşılaşma tekniğidir.
              <br /><br />
              <span style={{ color: '#e65100', fontWeight: 700 }}>“Uçan Tokat”</span> anlamına gelen Flying Slap; tarihteki muharebelerde karşı tarafın askerlerini en önde karşılayan, dünyaca ünlü Osmanlı tokadından esinlenerek kullanılmıştır.
            </>
          )}
          features={[]}
        />

        <div style={{ height: isMobile ? 24 : 48, backgroundColor: '#ffffff' }} />

        <DarkSection
          image={drone3Img}
          number="03"
          label="Global"
          title="DÜNYADA"
          titleColor="#e65100"
          subtitle="DRON FUTBOLU"
          text={(
            <>
              Güney Kore'den dünyaya yayılan bu disiplin, 2016'daki ilk gösteri maçından bu yana Amerika Birleşik Devletleri, Japonya, Çin ve Fransa da dahil olmak üzere 30'dan fazla ülkeye yayılmış durumda.
              <br /><br />
              2023’ten günümüze artık her yıl dünya çapında düzenlenen 30'dan fazla dronesoccer turnuvası bulunmaktadır. Şimdiden uluslararası federasyonlara ve dünya şampiyonlarına sahip bu spor çok yakında olimpiyat oyunlarının vazgeçilmez bir parçası olmaya adaydır.
              <br /><br />
              <span style={{ color: '#e65100', fontWeight: 700 }}>
                Drone futbolu, çarpışmaların sürekli olduğu hızlı tempolu bir hava sporudur. Müsabakalarda takım başarısı tamamen koordineli stratejiye bağlıdır.
              </span>
            </>
          )}
          features={[]}
          badge={{ value: 'N-TYPE', label: 'Kapsül' }}
          reversed
        />

        <div style={{ height: isMobile ? 24 : 48, backgroundColor: '#ffffff' }} />

        <DarkSection
          image={drone2Img}
          number="04"
          label="Türkiye"
          title="ÜLKEMİZDE"
          titleColor="#e65100"
          subtitle="DRONE FUTBOLU"
          text={(
            <>
              2024 yılında Türkiye Hava Sporları Federasyonu çatısı altında kurulan Drone Soccer Türkiye, ülke çapındaki turnuvaları başlatmış ve kurumsallaştırmıştır.
              <br /><br />
              İstanbul İl Milli Eğitim Müdürlüğümüzün 2024 yılında başlattığı “Köklerden Göklere” projesi kapsamında kulüpler hızla çoğalmış ve 2025 yılında ilk kez Drone Soccer Türkiye 1. Ulusal Turnuvası gerçekleştirilmiştir.
              <br /><br />
              <span style={{ color: '#e65100', fontWeight: 700 }}>
                2026 yılında Flying Slap Spor Kulübümüz Türkiye Hava Sporları Federasyonu tarafından tescillenmiş ve profesyonel antrenörleri ile kulüp çalışmalarını başlatmıştır.
              </span>
            </>
          )}
          features={[]}
        />

        {/* Space before footer */}
        <div style={{ height: isMobile ? 24 : 48, backgroundColor: '#ffffff' }} />
      </div>
    </div>
  );
};

export default About;
